import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BarChart2, Activity, Users, DollarSign, Gamepad2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const StatCard = ({ icon: Icon, label, value, suffix }) => (
  <div className="flex items-center gap-3 bg-black/40 border border-white/10 rounded-xl px-4 py-3">
    <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center">
      <Icon className="text-purple-300" size={18} />
    </div>
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wide text-gray-400">{label}</span>
      <span className="text-sm font-semibold text-white">
        {value}
        {suffix ? <span className="ml-1 text-[10px] text-gray-400">{suffix}</span> : null}
      </span>
    </div>
  </div>
);

const MiniBarList = ({ title, items, valueKey, labelKey }) => (
  <div className="bg-black/30 border border-white/10 rounded-xl p-4">
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs font-semibold text-gray-200">{title}</span>
    </div>
    {(!items || items.length === 0) && (
      <div className="text-[11px] text-gray-500">No data yet.</div>
    )}
    <div className="space-y-2 max-h-64 overflow-y-auto">
      {items?.map((item) => {
        const value = item[valueKey] || 0;
        const label = item[labelKey] || 'Untitled';
        const percentage = value <= 0 ? 0 : Math.min(100, (value / (items[0]?.[valueKey] || 1)) * 100);
        return (
          <div key={item.id || item.game_id || item.user_id} className="space-y-1">
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-gray-300 truncate max-w-[170px]" title={label}>{label}</span>
              <span className="text-gray-400">
                {valueKey === 'creator_revenue' ? `$${value.toFixed(4)}` : value}
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

const AdminAnalyticsPanel = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${BACKEND_URL}/api/admin/analytics`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setData(response.data);
      } catch (err) {
        console.error('Error loading admin analytics:', err);
        setError(err.response?.data?.detail || 'Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, []);

  if (loading) {
    return <div className="text-xs text-gray-400">Loading analytics...</div>;
  }

  if (error) {
    return <div className="text-xs text-red-400">{error}</div>;
  }

  if (!data) {
    return <div className="text-xs text-gray-400">No analytics data available.</div>;
  }

  const { kpis, revenue_by_day, new_users_by_day, top_games_by_revenue, top_creators_by_revenue } = data;

  const hasAdsenseClient = !!process.env.REACT_APP_ADSENSE_PUBLISHER_ID;
  const hasAdsenseSlot = !!process.env.REACT_APP_ADSENSE_GAME_SLOT_ID;
  const adsenseStatus = hasAdsenseClient && hasAdsenseSlot
    ? 'AdSense env vars detected'
    : 'AdSense env vars missing or incomplete';

  // Runtime ad mode (set by AdBanner at client-side): 'adsense' | 'fallback' | undefined
  let runtimeAdMode = 'unknown';
  if (typeof window !== 'undefined' && window.__ad_mode) {
    runtimeAdMode = window.__ad_mode;
  }

  const latestRevenueDay = revenue_by_day?.[revenue_by_day.length - 1];
  const latestUsersDay = new_users_by_day?.[new_users_by_day.length - 1];

  return (
    <div className="mt-6 border border-cyan-500/40 bg-cyan-900/20 rounded-2xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-cyan-500/20 flex items-center justify-center">
            <BarChart2 className="text-cyan-300" size={16} />
          </div>
          <div>
            <h3 className="text-xs font-semibold text-cyan-100 uppercase tracking-wide">Admin Analytics</h3>
            <p className="text-[11px] text-cyan-100/80">Only visible to trcd1@icloud.com</p>
          </div>
        </div>
        <span className="flex items-center gap-2 text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-100 border border-cyan-500/40">
          <span>Realtime (experimental)</span>
        </span>
      </div>

      {/* Tiny admin-only AdSense debug (no secrets) */}
      <div className="mb-3 text-[10px] text-cyan-100/80 flex flex-wrap gap-3 items-center">
        <span className="px-2 py-0.5 rounded-full bg-black/40 border border-cyan-500/40">
          AdSense env: {adsenseStatus}
        </span>
        {hasAdsenseClient && hasAdsenseSlot && (
          <span className="px-2 py-0.5 rounded-full bg-black/40 border border-emerald-500/40">
            Publisher & slot configured (values hidden)
          </span>
        )}
        <span
          className={`px-2 py-0.5 rounded-full border ${
            runtimeAdMode === 'adsense'
              ? 'bg-green-500/20 border-green-400 text-green-100'
              : runtimeAdMode === 'fallback'
                ? 'bg-amber-500/10 border-amber-400 text-amber-100'
                : 'bg-black/40 border-cyan-500/40 text-cyan-100'
          }`}
        >
          Ad runtime: {runtimeAdMode === 'adsense' ? 'AdSense live' : runtimeAdMode === 'fallback' ? 'Fallback house ads' : 'Unknown'}
        </span>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatCard
          icon={Users}
          label="Total Users"
          value={kpis.total_users}
        />
        <StatCard
          icon={Gamepad2}
          label="Total Games"
          value={kpis.total_games}
        />
        <StatCard
          icon={Activity}
          label="Total Plays"
          value={kpis.total_plays}
        />
        <StatCard
          icon={DollarSign}
          label="Creator Revenue (90%)"
          value={`$${kpis.total_creator_revenue.toFixed(4)}`}
        />
      </div>

      <div className="grid md:grid-cols-3 gap-4 mt-3">
        {/* Time series mini-overview */}
        <div className="space-y-3">
          <div className="bg-black/30 border border-white/10 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-200">Recent Revenue</span>
              {latestRevenueDay && (
                <span className="text-[10px] text-gray-400">{latestRevenueDay.label}</span>
              )}
            </div>
            {latestRevenueDay ? (
              <div className="text-sm font-semibold text-green-300">
                ${latestRevenueDay.value.toFixed(4)} / day
              </div>
            ) : (
              <div className="text-[11px] text-gray-500">No revenue yet.</div>
            )}
          </div>

          <div className="bg-black/30 border border-white/10 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-200">Recent Signups</span>
              {latestUsersDay && (
                <span className="text-[10px] text-gray-400">{latestUsersDay.label}</span>
              )}
            </div>
            {latestUsersDay ? (
              <div className="text-sm font-semibold text-purple-200">
                +{latestUsersDay.value} users / day
              </div>
            ) : (
              <div className="text-[11px] text-gray-500">No signups yet.</div>
            )}
          </div>

          <div className="bg-black/30 border border-white/10 rounded-xl p-4 text-[11px] text-gray-300 space-y-1">
            <div className="flex items-center justify-between">
              <span>Total Payments</span>
              <span className="font-semibold text-emerald-300">${kpis.total_payments.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Total Payouts</span>
              <span className="font-semibold text-emerald-200">${kpis.total_payouts.toFixed(4)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Platform Fees (10%)</span>
              <span className="font-semibold text-amber-300">${kpis.total_platform_fees.toFixed(4)}</span>
            </div>
          </div>
        </div>

        {/* Top games */}
        <MiniBarList
          title="Top Games by Revenue"
          items={(top_games_by_revenue || []).map((g) => ({
            ...g,
            id: g.game_id,
          }))}
          valueKey="creator_revenue"
          labelKey="title"
        />

        {/* Top creators */}
        <MiniBarList
          title="Top Creators by Revenue"
          items={(top_creators_by_revenue || []).map((c) => ({
            ...c,
            id: c.user_id,
            label: c.username || c.email,
          }))}
          valueKey="creator_revenue"
          labelKey="label"
        />
      </div>
    </div>
  );
};

export default AdminAnalyticsPanel;
